import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { isEmpty } from 'lodash';
import '../Todo.css';
import InfiniteScroll from 'react-infinite-scroll-component';
import SupportingDocumentTabel from './SupportingDocumentTable';
import { customStylesWhite, Page_Size as PageSize } from '../../../../Components/helper';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import * as supportingDocumentTodoAction from './store/SupportingDocumentToDo.action';
import * as supplierAction from '../../../Supplier/store/Supplier.action';
import AddEditSupplierDocument from '../../../Supplier/SupplierDocuments/AddEditSupplierDocument';
import DuplicateDocument from '../../../Supplier/SupplierDocuments/DuplicateDocument';
import ConfirmationPopup from '../../../../Components/ModalPopup/ConfirmationPopup';
import DocumentApproveRejectFooter from '../../../Supplier/SupplierDocuments/DocumentApproveRejectFooter';
import PreviewModal from '../../../../Components/ModalPopup/PreviewModal';
import Loader from '../../../../Components/Utility/Loader/Loader';

function SupportingDocument() {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const expandSearchRefBin = useRef(null);
  const searchDebounceTimerRef = useRef(null);
  const [Documents, setDocuments] = useState([]);
  const [allSelectedDocuments, setAllSelectedDocuments] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedRowMeta, setSelectedRowMeta] = useState({
    supplierId: null,
    factoryId: null,
  });
  const [addEditDocumentOpen, setAddEditDocumentOpen] = useState({
    isOpen: false,
    isEdit: false,
    isRenew: false,
    isTemplate: false,
  });
  const [editDocument, setEditDocument] = useState({
    data: null,
    index: -1,
    id: '',
  });

  const [duplicateState, setDuplicateState] = useState({
    isOpen: false,
    data: null,
  });
  const [deleteState, setDeleteState] = useState({
    isOpen: false,
    ids: [],
    items: [],
    loading: false,
  });
  const [archiveState, setArchiveState] = useState({
    isOpen: false,
    ids: [],
    items: [],
    comment: '',
    loading: false,
  });
  const [selectedDocType, setSelectedDocType] = useState([]);
  const [selectedDocStatus, setSelectedDocStatus] = useState([]);
  const [docId, setDocId] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(false);
  const [selectedFileToPreview, setSelectedFileToPreview] = useState(null);
  const [renewalData, setRenewalData] = useState(null);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const handleIconBinClick = () => {
    expandSearchRefBin?.current?.classList?.toggle('active-input');
  };

  const selectStyleOne = {
    ...customStylesWhite,
    control: () => ({
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      background: '#FFF',
      height: '30px',
      border: 'none',
    }),
  };

  const supportingDocument = useSelector((state) => state?.SupportingDocumentTodoListData?.supportingDocumentTodoListData || []);
  const totalCount = useSelector((state) => state?.SupportingDocumentTodoListData?.supportingDocumentTodoListTotalCount || 0);
  const filterValues = useSelector((state) => state?.SupportingDocumentTodoListData?.supportingDocumentTodoFilterValues || {});
  const userInfo = useSelector((state) => state?.UserDetails?.userInfo || {});

  useEffect(() => {
    const totalVisible = supportingDocument?.length || 0;
    const nextAllSelected = totalVisible > 0 && supportingDocument?.every((item) => Documents?.includes(item?._id));

    if (allSelectedDocuments !== nextAllSelected) {
      setAllSelectedDocuments(nextAllSelected);
    }
  }, [Documents, supportingDocument, allSelectedDocuments]);

  const hasMore = useMemo(() => {
    const currentLength = supportingDocument?.length || 0;
    return currentLength < (totalCount || 0);
  }, [supportingDocument?.length, totalCount]);

  const tagsOptions = useMemo(
    () => [
      {
        label: t('BTN_CLOCK.THREE_MONTHS'),
        value: '3',
        isDisabled: false,
      },
      {
        label: t('BTN_CLOCK.SIX_MONTHS'),
        value: '6',
        isDisabled: false,
      },
      {
        label: t('BTN_CLOCK.NINE_MONTHS'),
        value: '9',
        isDisabled: false,
      },
      { label: 'Expired', value: 'expired' },
      { label: 'Today', value: 'today' },
    ],
    [t],
  );

  const fetchList = useCallback(
    async (payload = {}) => {
      const params = {
        pageIndex: payload.pageIndex || filterValues?.pageIndex || 1,
        pageSize: payload.pageSize || filterValues?.pageSize || PageSize,
        searchText: payload.searchText ?? filterValues?.searchText,
        sortOrder: payload.sortOrder ?? filterValues?.sortOrder,
        sortPropertyName: payload.sortPropertyName ?? filterValues?.sortPropertyName,
        expiry: payload.expiry ?? filterValues?.expiry,
        timezone: Intl.DateTimeFormat()?.resolvedOptions?.()?.timeZone,
        isScrolled: Boolean(payload?.isScrolled),
      };

      await dispatch(supportingDocumentTodoAction.getSupportingDocumentTodoList(params));
    },
    [dispatch, filterValues],
  );

  const refreshPage = useCallback(async () => {
    await dispatch(
      supportingDocumentTodoAction.filterSortData({
        pageIndex: 1,
      }),
    );
    await fetchList({ pageIndex: 1, isScrolled: false });
  }, [dispatch, fetchList]);

  const updateSupplierDocument = useCallback(async () => {
    await refreshPage();
  }, [refreshPage]);

  const handleDuplicateById = useCallback((item) => {
    setDuplicateState({ isOpen: true, data: item || null });
  }, []);

  const toggleDuplicate = useCallback(() => {
    setDuplicateState({ isOpen: false, data: null });
  }, []);

  const getSelectedRows = useCallback(
    (ids) => {
      const idSet = new Set(ids || []);
      return (supportingDocument || []).filter((row) => idSet.has(row?._id));
    },
    [supportingDocument],
  );

  const openDeleteConfirmation = useCallback((id, item) => {
    setDeleteState({ isOpen: true, ids: id ? [id] : [], items: item ? [item] : [], loading: false });
  }, []);

  const openBulkDeleteConfirmation = useCallback(() => {
    const selected = getSelectedRows(Documents);
    setDeleteState({ isOpen: true, ids: Documents || [], items: selected, loading: false });
  }, [Documents, getSelectedRows]);

  const groupRowsBySupplier = useCallback((rows, mapper) => {
    const groups = new Map();

    (rows || []).forEach((row) => {
      const supplierId = row?.supplierId;
      if (!supplierId) return;

      const key = String(supplierId);
      const current = groups.get(key) || mapper.createGroup(supplierId);
      mapper.addRow(current, row);
      groups.set(key, current);
    });

    return Array.from(groups.values());
  }, []);

  const closeDeleteConfirmation = useCallback(() => {
    setDeleteState({ isOpen: false, ids: [], items: [], loading: false });
  }, []);

  const deleteDocumentConfirmed = useCallback(async () => {
    if (!Array.isArray(deleteState?.ids) || deleteState.ids.length === 0) return;
    try {
      setDeleteState((prev) => ({ ...prev, loading: true }));
      const deletePayload = {
        docIds: deleteState.ids,
      };
      await dispatch(supportingDocumentTodoAction.bulkDeleteSupportingDocumentTodos(deletePayload.docIds, t));
      closeDeleteConfirmation();
      await refreshPage();
      setDocuments([]);
    } finally {
      setDeleteState((prev) => ({ ...prev, loading: false }));
    }
  }, [deleteState?.ids, closeDeleteConfirmation, refreshPage, dispatch, t]);

  const handleArchiveCard = useCallback((id, item) => {
    setArchiveState({
      isOpen: true,
      ids: id ? [id] : [],
      items: item ? [item] : [],
      comment: '',
      loading: false,
    });
  }, []);

  const handleBulkArchive = useCallback(() => {
    const selected = getSelectedRows(Documents);
    setArchiveState({
      isOpen: true,
      ids: Documents || [],
      items: selected,
      comment: '',
      loading: false,
    });
  }, [Documents, getSelectedRows]);

  const closeArchivePopup = useCallback(() => {
    setArchiveState({
      isOpen: false,
      ids: [],
      items: [],
      comment: '',
      loading: false,
    });
  }, []);

  const onArchiveCommentChange = useCallback((e) => {
    const value = e?.target?.value ?? '';
    setArchiveState((prev) => ({ ...prev, comment: value }));
  }, []);

  const confirmArchive = useCallback(async () => {
    if (!Array.isArray(archiveState?.ids) || archiveState.ids.length === 0) return;
    try {
      setArchiveState((prev) => ({ ...prev, loading: true }));

      const selectedRows = Array.isArray(archiveState?.items) && archiveState.items.length > 0 ? archiveState.items : getSelectedRows(archiveState.ids);

      const isSingle = (selectedRows || []).length === 1;
      const singleRow = isSingle ? selectedRows[0] : null;
      const nextIsArchived = isSingle ? !singleRow?.isArchived : true;
      const archiveComment = nextIsArchived ? archiveState?.comment : '';

      const archivePayload = {
        documentId: archiveState.ids,
        isArchived: nextIsArchived,
        archiveComment,
      };

      await dispatch(supplierAction.archiveUnarchiveDocument(archivePayload, t));

      closeArchivePopup();
      await refreshPage();
      setDocuments([]);
    } finally {
      setArchiveState((prev) => ({ ...prev, loading: false }));
    }
  }, [archiveState?.comment, archiveState?.ids, archiveState?.items, closeArchivePopup, dispatch, getSelectedRows, refreshPage, t]);

  useEffect(() => {
    fetchList({ pageIndex: 1, isScrolled: false });

    return () => {
      if (searchDebounceTimerRef?.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearchChange = async (e) => {
    const searchText = e?.target?.value || '';

    await dispatch(
      supportingDocumentTodoAction.filterSortData({
        searchText,
        pageIndex: 1,
      }),
    );

    if (searchDebounceTimerRef?.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      fetchList({ searchText, pageIndex: 1, isScrolled: false });
    }, 400);
  };

  const onSearchKeyDown = (e) => {
    if (e?.key !== 'Enter') return;
    e.preventDefault();

    const searchText = e?.currentTarget?.value || '';
    if (searchDebounceTimerRef?.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }
    fetchList({ searchText, pageIndex: 1, isScrolled: false });
  };

  const onExpiryChange = async (option) => {
    const expiry = option?.value || '';
    await dispatch(
      supportingDocumentTodoAction.filterSortData({
        expiry,
        pageIndex: 1,
      }),
    );
    await fetchList({ expiry, pageIndex: 1, isScrolled: false });
  };

  const onSortColumn = async (sortPropertyName) => {
    const currentName = filterValues?.sortPropertyName;
    const currentOrder = (filterValues?.sortOrder || '').toLowerCase();
    const nextOrder = currentName === sortPropertyName && currentOrder === 'asc' ? 'desc' : 'asc';

    await dispatch(
      supportingDocumentTodoAction.filterSortData({
        sortPropertyName,
        sortOrder: nextOrder,
        pageIndex: 1,
      }),
    );
    await fetchList({ sortPropertyName, sortOrder: nextOrder, pageIndex: 1, isScrolled: false });
  };

  const handlePageNextInScroll = async () => {
    if (!hasMore) return;
    const nextPage = Number(filterValues?.pageIndex || 1) + 1;
    await dispatch(supportingDocumentTodoAction.filterSortData({ pageIndex: nextPage }));
    await fetchList({ pageIndex: nextPage, isScrolled: true });
  };

  const selectDocumentCheckBox = async (e, id, item) => {
    if (e.target.checked) {
      setDocuments((prevDocuments) => {
        const selectedItems = [...prevDocuments];
        if (!selectedItems?.includes(id)) {
          selectedItems.push(id);
        }
        return selectedItems;
      });
      setSelectedDocType((prev) => [...prev, item?.type]);
      if (item?.documentType?._id) {
        setDocId((prev) => [...prev, item.documentType._id]);
      }
    } else {
      setAllSelectedDocuments(false);
      setDocuments((prevDocuments) => prevDocuments.filter((doc) => doc !== id));
      setSelectedDocType((prev) => {
        const idx = prev.indexOf(item?.type);
        if (idx > -1) {
          const next = [...prev];
          next.splice(idx, 1);
          return next;
        }
        return prev;
      });
      if (item?.documentType?._id) {
        setDocId((prev) => prev.filter((did) => did !== item.documentType._id));
      }
    }
  };

  const handleSelectAllDocuments = () => {
    const totalVisible = supportingDocument?.length || 0;
    const isAllSelected = totalVisible > 0 && supportingDocument?.every((item) => Documents?.includes(item?._id));

    if (isAllSelected) {
      setDocuments([]);
      setSelectedDocType([]);
      setDocId([]);
    } else {
      const nextIds = new Set(Documents);
      const nextTypes = [...selectedDocType];
      const nextDocIds = [...docId];
      for (const item of supportingDocument || []) {
        if (item?._id && !nextIds.has(item._id)) {
          nextIds.add(item._id);
          nextTypes.push(item?.type);
          if (item?.documentType?._id) nextDocIds.push(item.documentType._id);
        }
      }
      setDocuments(Array.from(nextIds));
      setSelectedDocType(nextTypes);
      setDocId(nextDocIds);
    }
  };
  const EditDocumentOpenClose = (event, docId, index, item) => {
    setOpenModal(item?.type || 'Document');
    if (event) event.preventDefault();

    setSelectedRowMeta({
      supplierId: item?.supplierId || null,
      factoryId: Array.isArray(item?.factoryIds) ? item?.factoryIds?.[0] || null : null,
    });

    setAddEditDocumentOpen((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
      isEdit: !!(docId !== null && docId !== undefined),
      isRenew: false,
      docId,
    }));
    let editData = { data: null, id: null };
    if (docId !== null && docId !== undefined) {
      const data = supportingDocument.filter((ele) => ele._id === docId);
      if (data?.[0]) {
        editData = { data: data[0], id: docId, index };
      }
    }
    setEditDocument(editData);
  };

  const handleApproveRejectDoc = useCallback(
    async (value) => {
      const docIds = value[1];
      if (!docIds || !docIds.length) return;
      const firstDoc = supportingDocument.find((d) => d._id === docIds[0]);
      const supplierId = firstDoc?.supplierId || firstDoc?.supplier?._id || firstDoc?.supplier;
      const payload = {
        supplierId,
        docIds,
        status: selectedDocType?.includes('Template') ? (value[2]?.status === '1' ? 'Approved' : 'Rejected') : value[2]?.status?.name,
        commentDate: value[2]?.commentDate,
        comment: value[2]?.comment,
      };
      const res = await dispatch(supplierAction.approveRejectDocuments(payload, t));
      if (res === 200) {
        setDocuments([]);
        setSelectedDocType([]);
        setDocId([]);
        await refreshPage();
      }
    },
    [dispatch, supportingDocument, selectedDocType, refreshPage, t],
  );

  useEffect(() => {
    if (Documents.length > 0) {
      if (debounceTimeout) clearTimeout(debounceTimeout);
      const newTimeout = setTimeout(async () => {
        if (selectedDocType.length > 0 && selectedDocType.includes('Document')) {
          const res = await dispatch(supplierAction.retrieveDocStatus({ docIds: docId }));
          if (res) setSelectedDocStatus(res);
        }
      }, 1000);
      setDebounceTimeout(newTimeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Documents, selectedDocType]);

  const handlePreviewDocument = useCallback((item) => {
    setSelectedFileToPreview(item);
    setPreviewDoc(true);
  }, []);

  const togglePreview = useCallback(() => {
    setSelectedFileToPreview(null);
    setPreviewDoc(false);
  }, []);

  const handleRenewDocument = useCallback(
    (event, item, docId) => {
      if (event) event.preventDefault();
      setOpenModal(item?.type || 'Document');
      setSelectedRowMeta({
        supplierId: item?.supplierId || null,
        factoryId: Array.isArray(item?.factoryIds) ? item?.factoryIds?.[0] || null : null,
      });
      setRenewalData({ doc: item, id: docId });
      setAddEditDocumentOpen((prev) => ({
        ...prev,
        isOpen: true,
        isEdit: false,
        isRenew: true,
        docId,
      }));
      setEditDocument({ data: item, id: docId, index: -1 });
    },
    [],
  );


  const handleDownloadSelected = async () => {
    setDownloadLoading(true);
    try {
      await dispatch(supportingDocumentTodoAction.downloadSelectedSupportingDocumentTodos(Documents, t));
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDownloadAll = async () => {
    setDownloadLoading(true);
    try {
      await dispatch(
        supportingDocumentTodoAction.downloadAllSupportingDocumentTodos(
          { filterValues: { searchText: filterValues?.searchText, expiry: filterValues?.expiry } },
          t,
        ),
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  const renderDownloadPopover = () => (
    <ul>
      <li>
        <button type="button" className="p-0 btn" onClick={handleDownloadSelected} disabled={!Documents?.length || downloadLoading}>
          {downloadLoading ? t('COMMON_LABEL.DOWNLOADING') : t('COMMON_LABEL.DOWNLOAD_SELECTED_DATA')}
        </button>
      </li>
      <li>
        <button type="button" className="p-0 btn" onClick={handleDownloadAll} disabled={downloadLoading}>
          {downloadLoading ? t('COMMON_LABEL.DOWNLOADING') : t('COMMON_LABEL.DOWNLOAD_ALL_DATA')}
        </button>
      </li>
    </ul>
  );
  return (
    <>
      <div>
        <div className="page-title-filter sticky">
          <div className="title-container d-flex align-items-center">
            <h4 className="title">{t('REPORTS.SUPPLIER_DOCUMENT')}</h4>
          </div>

          <div className="search-filter">
            <div className="document-todo-select  w-200 mr-16">
              <Select
                options={tagsOptions}
                isClearable
                classNamePrefix="common-select"
                getOptionLabel={(option) => `${option.label}`}
                getOptionValue={(option) => `${option.value}`}
                placeholder="Expiring in"
                styles={selectStyleOne}
                value={tagsOptions?.find((el) => el.value === filterValues?.expiry) || null}
                onChange={onExpiryChange}
              />
            </div>
            <div ref={expandSearchRefBin} className="common-expand-search supporting-doc-todo">
              <button type="button" onClick={handleIconBinClick} className="btn btn-toggle">
                <span className="sr-only">Search</span>
                <span className="icon-Magnifying-Glass-Solid" />
              </button>

              <input
                name="search"
                className="form-control"
                type="text"
                value={filterValues?.searchText || ''}
                onChange={onSearchChange}
                onKeyDown={onSearchKeyDown}
                placeholder={t('DEVLOPMENT_ORDER.TESTING.PRODUCT_TESTING.SEARCH')}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={`floting-actions${!isEmpty(Documents) ? ' component-bottom' : ''}`}>
        <OverlayTrigger
          placement="top"
          trigger="click"
          rootClose
          overlay={
            <Popover className="custom-popover file-download-popover">
              <Popover.Content>{renderDownloadPopover()}</Popover.Content>
            </Popover>
          }
        >
          <button type="button" className="btn" aria-label="Download">
            <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.DOWNLOAD')}>
              <i className="icon-Download" />
            </CustomTooltip>
          </button>
        </OverlayTrigger>
        <CustomTooltip tooltipText="Archive">
          <button className="btn p-0 text-decoration-none" onClick={handleBulkArchive} type="button" aria-label="Bulk archive" disabled={!Documents?.length}>
            <i className="icon-Archive f-20" />
          </button>
        </CustomTooltip>
        <CustomTooltip tooltipText="Delete">
          <button
            className="btn p-0 text-decoration-none"
            onClick={openBulkDeleteConfirmation}
            type="button"
            aria-label="Bulk delete"
            disabled={!Documents?.length}
          >
            <i className="icon-Delete f-20" />
          </button>
        </CustomTooltip>
      </div>
      <div id="supportingDocumentTableContainer" className="grading-group-listing supporting-doc-todo-page mt-24 ml-24 mr-24">
        <InfiniteScroll
          dataLength={supportingDocument?.length || 0}
          hasMore={hasMore}
          next={() => handlePageNextInScroll()}
          scrollableTarget="supportingDocumentTableContainer"
          className="fixed-col-container"
          loader={<div className="text-center py-3"><Loader /></div>}
        >
          <SupportingDocumentTabel
            supportingDocument={supportingDocument}
            supplierId={null}
            handleSelectAll={handleSelectAllDocuments}
            selectDocumentCheckBox={selectDocumentCheckBox}
            allSelectedDocuments={allSelectedDocuments}
            Documents={Documents}
            onDelete={openDeleteConfirmation}
            onEdit={EditDocumentOpenClose}
            onPreview={handlePreviewDocument}
            onRenew={handleRenewDocument}
            onSortColumn={onSortColumn}
            loading={Boolean(filterValues?.loading)}
            CostingAddEditAccess={false}
            CostingDeleteAccess={false}
            archiveSection={false}
            handleArchiveCard={handleArchiveCard}
            handleDuplicateById={handleDuplicateById}
            isFactoryLevel={false}
            sortTable={{ sortOrder: filterValues?.sortOrder, sortPropertyName: filterValues?.sortPropertyName }}
            handleApproveRejectDoc={handleApproveRejectDoc}
          />
        </InfiniteScroll>
      </div>
      {addEditDocumentOpen?.isOpen && openModal === 'Document' && (
        <AddEditSupplierDocument
          onOpen={addEditDocumentOpen.isOpen}
          toggle={EditDocumentOpenClose}
          isEditMode={addEditDocumentOpen.isEdit}
          isRenewMode={addEditDocumentOpen.isRenew}
          data={editDocument.data}
          index={editDocument.index}
          supplierId={selectedRowMeta?.supplierId}
          refreshPage={refreshPage}
          updateSupplierDocument={updateSupplierDocument}
          factoryId={selectedRowMeta?.factoryId}
          isFactoryLevel={false}
          setSupportingData={() => {}}
          docs={[]}
          setDocs={() => {}}
          handleAddDocument={() => {}}
          userInfo={userInfo}
          renewalData={renewalData}
        />
      )}

      {duplicateState?.isOpen && (
        <DuplicateDocument
          onOpen={duplicateState.isOpen}
          toggle={toggleDuplicate}
          data={duplicateState.data}
          supplierId={duplicateState?.data?.supplierId}
          refreshPage={refreshPage}
        />
      )}

      <ConfirmationPopup
        onOpen={deleteState?.isOpen}
        onConfirm={deleteDocumentConfirmed}
        title={t('SUPPLIER_DOCUMENTS.DELETE_CONFIRMATION_TITLE')}
        message={t('PARTNER_OVERVIEW.DELETE_MESSAGE')}
        toggle={closeDeleteConfirmation}
        loading={deleteState?.loading}
      />

      <ConfirmationPopup
        onOpen={archiveState?.isOpen}
        onConfirm={confirmArchive}
        title={`${
          archiveState?.items?.length === 1 && archiveState?.items?.[0]?.isArchived ? t('PARTNER_OVERVIEW.UNARCHIVE') : t('PARTNER_OVERVIEW.ARCHIEVE')
        } Document`}
        message={`Are you sure you want to ${
          archiveState?.items?.length === 1 && archiveState?.items?.[0]?.isArchived ? t('PARTNER_OVERVIEW.unarchive') : t('PARTNER_OVERVIEW.archive')
        } the selected document?`}
        secondMessage={archiveState?.items?.length === 1 && archiveState?.items?.[0]?.isArchived ? t('PARTNER_OVERVIEW.UNARCHIVE_MESSAGE') : ''}
        toggle={closeArchivePopup}
        isCommentOn={!(archiveState?.items?.length === 1 && archiveState?.items?.[0]?.isArchived)}
        commentText={onArchiveCommentChange}
        comment={archiveState?.comment}
        showCommentHeader
        fromSupplier
        loading={archiveState?.loading}
      />

      {!isEmpty(Documents) && (
        <DocumentApproveRejectFooter
          devOrderId={Documents}
          onSave={(...data) => handleApproveRejectDoc(data)}
          selectedIdArray={Documents}
          fromSupplierDocumentComponent
          selectedDocStatus={selectedDocStatus}
          isDisabledState={isEmpty(Documents)}
          selectedDocType={selectedDocType}
        />
      )}

      {previewDoc && <PreviewModal isSupplierLevel onOpen={previewDoc} data={selectedFileToPreview} moduleName="specTemplate" toggle={togglePreview} />}
    </>
  );
}

export default SupportingDocument;
